import React, { useState, useEffect } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { anyPass, concat, filter, length, uniqBy, prop, propEq } from "ramda";
import { notBlank } from "../../util/ext";
import { isContentGroupsHome, isAnyReview, useView } from "./organizeUtil";

import DragItemTypes from "./DragItemTypes";
import { useDragLayer } from "react-dnd";
import { useSelected, useDnD } from "./OrganizeProvider";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import ImageIcon from "@mui/icons-material/Image";

const useStyles = makeStyles(theme => ({
  card: {
    width: 250,
    height: 75,
    padding: theme.spacing(2),
    boxShadow: "0px 0px 20px rgba(0, 0, 0, 0.15)",
  },

  icon: {
    fontSize: 30,
    marginRight: theme.spacing(2),
  },

  grid: {
    height: "100%",
  },

  total: {
    color: theme.palette.primary.main,
    fontWeight: "bold",
  },
}));

const layerStyles = {
  position: "fixed",
  pointerEvents: "none",
  zIndex: 100,
  left: 0,
  top: 0,
  width: "100%",
  height: "100%",
};

const getItemStyles = (initialCursorOffset, initialOffset, currentOffset) => {
  if (!initialOffset || !currentOffset || !initialCursorOffset) {
    return {
      display: "none",
    };
  }

  // const x = initialCursorOffset?.x + (currentOffset.x - initialOffset.x);
  // const y = initialCursorOffset?.y + (currentOffset.y - initialOffset.y);
  const x = initialCursorOffset?.x + (currentOffset.x - 10 - initialOffset.x);
  const y = initialCursorOffset?.y + (currentOffset.y - 10 - initialOffset.y);
  const transform = `translate(${x}px, ${y}px)`;

  return {
    transform,
    WebkitTransform: transform,
  };
};

const DragCard = ({ total }) => {
  const classes = useStyles();
  const view = useView();
  const dnd = useDnD();
  const [action, setAction] = useState("Move");
  useEffect(() => {
    if (notBlank(dnd.state.dragAction)) {
      setAction(dnd.state.dragAction);
    } else {
      setAction(
        anyPass([isAnyReview, isContentGroupsHome])(view) ? "Copy" : "Move",
      );
    }
  }, [view, dnd.state.dragAction]);
  return (
    <Card variant="outlined" className={classes.card}>
      <Grid
        classes={{ root: classes.grid }}
        container
        justifyContent="center"
        alignItems="center"
      >
        <Grid item>
          <ImageIcon color="primary" classes={{ root: classes.icon }} />
        </Grid>
        <Grid item style={{ fontSize: 20 }}>
          {action} <span className={classes.total}>{total}</span>
          {total > 1 ? " items" : " item"}
        </Grid>
      </Grid>
    </Card>
  );
};

const ContentCard = ({ item }) => {
  const { selectedContent } = useSelected();
  const [total, setTotal] = useState("-");
  useEffect(() => {
    if (notBlank(item)) {
      const dropItems = uniqBy(prop("id"), concat([item], selectedContent));
      const totalItems = length(dropItems);
      setTotal(totalItems);
    }
  }, [item, selectedContent]);

  return <DragCard total={total} />;
};

const CollectionCard = ({ item }) => {
  const { selectedContentGroups } = useSelected();

  const [total, setTotal] = useState("-");
  useEffect(() => {
    if (notBlank(item)) {
      const selectedCollections = filter(
        propEq("mediaType", "collection"),
        selectedContentGroups,
      );
      const dropItems = uniqBy(prop("id"), concat([item], selectedCollections));
      const totalItems = length(dropItems);
      setTotal(totalItems);
    }
  }, [item, selectedContentGroups]);

  return <DragCard total={total} />;
};

const AnthologyCard = ({ item }) => {
  const { selectedContentGroups } = useSelected();

  const [total, setTotal] = useState("-");
  useEffect(() => {
    if (notBlank(item)) {
      const selectedAnthologies = filter(
        propEq("mediaType", "collection"),
        selectedContentGroups,
      );
      const dropItems = uniqBy(prop("id"), concat([item], selectedAnthologies));
      const totalItems = length(dropItems);
      setTotal(totalItems);
    }
  }, [item, selectedContentGroups]);

  return <DragCard total={total} />;
};

const renderItem = (type, item) => {
  switch (type) {
    case DragItemTypes.CONTENT:
      return <ContentCard item={item} itemType={type} />;
    case DragItemTypes.COLLECTION:
      return <CollectionCard item={item} itemType={type} />;
    case DragItemTypes.ANTHOLOGY:
      return <AnthologyCard item={item} itemType={type} />;
    default:
      return null;
  }
};

const CustomDragLayer = () => {
  //-- Implementation Details:
  // https://stackoverflow.com/questions/69440259/how-to-implement-react-dnd-usedraglayer
  const {
    item,
    itemType,
    isDragging,
    initialCursorOffset,
    initialFileOffset,
    currentFileOffset,
  } = useDragLayer(monitor => ({
    item: monitor.getItem(),
    itemType: monitor.getItemType(),
    initialCursorOffset: monitor.getInitialClientOffset(),
    initialFileOffset: monitor.getInitialSourceClientOffset(),
    currentFileOffset: monitor.getSourceClientOffset(),
    isDragging: monitor.isDragging(),
  }));

  if (!isDragging) {
    return null;
  }

  return (
    <div style={layerStyles}>
      <div
        style={getItemStyles(
          initialCursorOffset,
          initialFileOffset,
          currentFileOffset,
        )}
      >
        {renderItem(itemType, item)}
      </div>
    </div>
  );
};

export default CustomDragLayer;
